import React from 'react'

import AdvertisersSection from '@/app/components/PageBuilder/AdvertisersSection'
import ContentSection from '@/app/components/PageBuilder/ContentSection'
import Cta from '@/app/components/PageBuilder/CtaSection'
import Hero from '@/app/components/PageBuilder/HeroSection'
import SideBySideSection from '@/app/components/PageBuilder/SideBySideSection'
import { dataAttr } from '@/sanity/lib/utils'

type SectionComponent = React.FC<any>

type SectionBlocks = {
  [key: string]: SectionComponent
}

const SectionComponents: SectionBlocks = {
  advertisersSection: AdvertisersSection,
  contentSection: ContentSection,
  ctaSection: Cta,
  heroSection: Hero,
  sideBySideSection: SideBySideSection,
}

type SharedSectionProps = {
  block: {
    _key: string
    _type: 'sharedSectionReference'
    sharedSection?: {
      _id: string
      _type: 'sharedSection'
      name: string
      description?: string
      section?: {
        _type: string
        _key: string
        [key: string]: any
      }
    }
  }
  index: number
  pageId: string
  pageType: string
}

/**
 * SharedSection component renders a referenced shared section.
 * It resolves the shared section document and renders the actual section component
 * with proper Sanity presentation/visual editing data attributes.
 */
export default function SharedSection({ block, index, pageId, pageType }: SharedSectionProps) {
  const { sharedSection } = block

  // If the reference isn't resolved, show a placeholder
  if (!sharedSection || !sharedSection.section) {
    return (
      <div className="w-full bg-gray-100 text-center text-gray-500 p-20 rounded">
        Shared section reference is not configured or has been deleted
      </div>
    )
  }

  const section = sharedSection.section
  const SectionComponent = SectionComponents[section._type]

  // If the section type doesn't have a component, show error
  if (!SectionComponent) {
    return (
      <div className="w-full bg-gray-100 text-center text-gray-500 p-20 rounded">
        A &ldquo;{section._type}&rdquo; block hasn&apos;t been created
      </div>
    )
  }

  // Render the section with proper data attributes for visual editing
  // The outer wrapper points to the sharedSectionReference in the page's pageBuilder
  // The inner wrapper points to the actual section content in the sharedSection document
  return (
    <div
      data-sanity={dataAttr({
        id: pageId,
        type: pageType,
        path: `pageBuilder[_key=="${block._key}"]`,
      }).toString()}
    >
      <div
        data-sanity={dataAttr({
          id: sharedSection._id,
          type: sharedSection._type,
          path: 'section[0]',
        }).toString()}
      >
        {React.createElement(SectionComponent, {
          key: section._key,
          block: section,
          index: index,
          pageId: sharedSection._id,
          pageType: sharedSection._type,
        })}
      </div>
    </div>
  )
}
